import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  CategoryDTO, 
  SupplierDTO, 
  TaxDTO, 
  LocationDTO, 
  CreateItemResponse, 
  CreateItemDTO,
  fetchCategoriesAPI, 
  fetchSuppliersAPI, 
  fetchTaxesAPI, 
  fetchLocationsAPI, 
  createItemAPI 
} from "./itemsAPI";

export interface ApiResponse<T = unknown> {
  status: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]> | string;
  path?: string;
  method?: string;
  timestamp?: string;
}

interface ItemsState {
  categories: CategoryDTO[];
  suppliers: SupplierDTO[];
  taxes: TaxDTO[];
  locations: LocationDTO[];

  loadingLookups: boolean;
  creating: boolean;

  error: string | null;
  successMessage: string | null;
  lastCreatedId: string | null;
}

const initialState: ItemsState = {
  categories: [],
  suppliers: [],
  taxes: [],
  locations: [],
  loadingLookups: false,
  creating: false,
  error: null,
  successMessage: null,
  lastCreatedId: null,
};

/** Load all dropdowns together */
export const loadItemLookups = createAsyncThunk<
  { categories: CategoryDTO[]; suppliers: SupplierDTO[]; taxes: TaxDTO[]; locations: LocationDTO[] },
  void,
  { rejectValue: string }
>("items/loadLookups", async (_arg, { rejectWithValue }) => {
  try {
    const [c, s, t, l] = await Promise.all([
      fetchCategoriesAPI(),
      fetchSuppliersAPI(),
      fetchTaxesAPI(),
      fetchLocationsAPI(),
    ]);

    if (!c.status || !s.status || !t.status || !l.status) {
      const message =
        (!c.status && c.message) ||
        (!s.status && s.message) ||
        (!t.status && t.message) ||
        (!l.status && l.message) ||
        "Failed to load dropdowns";
      return rejectWithValue(message);
    }

    return {
      categories: c.data ?? [],
      suppliers: s.data ?? [],
      taxes: t.data ?? [],
      locations: l.data ?? [],
    };
  } catch {
    return rejectWithValue("Server error while loading dropdowns.");
  }
});

/** Create item */
export const createItem = createAsyncThunk<
  CreateItemResponse,
  CreateItemDTO,
  { rejectValue: string }
>("items/create", async (dto, { rejectWithValue }) => {
  try {
    const res: ApiResponse<CreateItemResponse> = await createItemAPI(dto);
    if (res.status && res.data) return res.data;
    return rejectWithValue(res.message || "Failed to create item");
  } catch {
    return rejectWithValue("Server error. Please try again later.");
  }
});

const itemsSlice = createSlice({
  name: "items",
  initialState,
  reducers: {
    clearItemsFeedback(state) {
      state.error = null;
      state.successMessage = null;
      state.lastCreatedId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // lookups
      .addCase(loadItemLookups.pending, (state) => {
        state.loadingLookups = true;
        state.error = null;
      })
      .addCase(loadItemLookups.fulfilled, (state, action) => {
        state.loadingLookups = false;
        state.categories = action.payload.categories;
        state.suppliers = action.payload.suppliers;
        state.taxes = action.payload.taxes;
        state.locations = action.payload.locations;
      })
      .addCase(loadItemLookups.rejected, (state, action) => {
        state.loadingLookups = false;
        state.error = action.payload || "Failed to load dropdowns";
      })

      // create
      .addCase(createItem.pending, (state) => {
        state.creating = true;
        state.error = null;
        state.successMessage = null;
        state.lastCreatedId = null;
      })
      .addCase(createItem.fulfilled, (state, action: PayloadAction<CreateItemResponse>) => {
        state.creating = false;
        state.successMessage = "Item saved successfully";
        state.lastCreatedId = action.payload.id;
      })
      .addCase(createItem.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload || "Failed to create item";
      });
  },
});

export const { clearItemsFeedback } = itemsSlice.actions;
export default itemsSlice.reducer;

/** Selectors */
export const selectItemsLookups = (s: any) => ({
  categories: s.items.categories as CategoryDTO[],
  suppliers: s.items.suppliers as SupplierDTO[],
  taxes: s.items.taxes as TaxDTO[],
  locations: s.items.locations as LocationDTO[],
  loading: s.items.loadingLookups as boolean,
});

export const selectItemsFeedback = (s: any) => ({
  creating: s.items.creating as boolean,
  error: s.items.error as string | null,
  successMessage: s.items.successMessage as string | null,
  lastCreatedId: s.items.lastCreatedId as string | null,
});
